import React, { useEffect, useState } from "react"
import { Card, CardBody, Row, Col, Input, Label, Button } from "reactstrap"
import { getUserFormData, postUser } from "../../services/Inventory/inventory"
import { toastError, toastSuccess } from "../../helpers/msg_helper"

/**
 * StaffFlow-parity user form: login + roles (menus) + company/group access + home company.
 * Group access grants the whole group (incl. companies added later); a company greyed out
 * is already granted via its group.
 */
const blank = {
  name: "", email: "", password: "", status: 1,
  roles: [], company_ids: [], group_ids: [], default_company_id: "",
}

const UserForm = ({ editUser, onDone }) => {
  const isEdit = !!editUser?.id
  const [opts, setOpts] = useState({ roles: [], groups: [], companies: [] })
  const [form, setForm] = useState(blank)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const r = await getUserFormData()
        setOpts({ roles: r?.roles || [], groups: r?.groups || [], companies: r?.companies || [] })
      } catch (e) { toastError(e?.message) }
    })()
  }, [])

  useEffect(() => {
    if (isEdit) {
      setForm({
        name: editUser.name || "", email: editUser.email || "", password: "", status: editUser.status ?? 1,
        roles: (editUser.roles || []).map(r => r.name),
        company_ids: (editUser.companies || []).map(c => c.id),
        group_ids: (editUser.groups || []).map(g => g.id),
        default_company_id: editUser.default_company_id || "",
      })
    } else {
      setForm(blank)
    }
  }, [editUser?.id])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const toggle = (field, value) => setForm(f => {
    const has = f[field].includes(value)
    return { ...f, [field]: has ? f[field].filter(x => x !== value) : [...f[field], value] }
  })

  // A company is granted directly OR via its group.
  const viaGroup = c => c.group_id != null && form.group_ids.includes(c.group_id)
  const accessibleCompanies = opts.companies.filter(c => form.company_ids.includes(c.id) || viaGroup(c))

  const save = async () => {
    if (!form.name.trim()) return toastError("Name is required")
    if (!form.email.trim()) return toastError("Email is required")
    if (!isEdit && (form.password || "").length < 8) return toastError("Password must be at least 8 characters")
    try {
      setSaving(true)
      const payload = {
        ...(isEdit ? { id: editUser.id } : {}),
        name: form.name, email: form.email,
        ...(form.password ? { password: form.password } : {}),
        roles: form.roles, company_ids: form.company_ids, group_ids: form.group_ids,
        default_company_id: form.default_company_id || null, status: form.status,
      }
      const res = await postUser(payload)
      toastSuccess(res?.message || (isEdit ? "User updated" : "User created"))
      onDone?.(true)
    } catch (e) { toastError(e?.response?.data?.message || e?.message) } finally { setSaving(false) }
  }

  const cube = code => (
    <span style={{ fontSize: 10, fontWeight: 700, color: "#6a7488", background: "#eef0f4", borderRadius: 4, padding: "1px 4px", marginRight: 6 }}>{code}</span>
  )

  return (
    <Card className="border"><CardBody>
      <h5 className="mb-1">{isEdit ? "Edit user" : "Add a user"}</h5>
      <div className="text-muted small mb-3">
        Create a login, give it <b>roles</b> (what menus they get) and <b>company access</b> (which entities they can open). The home company is the one they land on.
      </div>

      {/* Identity */}
      <Row>
        <Col md={4}><Label className="text-uppercase small text-muted">Name<span className="mandatory">*</span></Label><Input value={form.name} onChange={e => set("name", e.target.value)} /></Col>
        <Col md={4}><Label className="text-uppercase small text-muted">Email<span className="mandatory">*</span></Label><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} /></Col>
        <Col md={4}><Label className="text-uppercase small text-muted">Password{!isEdit ? <span className="mandatory">*</span> : null}</Label><Input type="password" autoComplete="new-password" value={form.password} placeholder={isEdit ? "unchanged" : "min 8 chars"} onChange={e => set("password", e.target.value)} /></Col>
      </Row>

      <Row className="mt-3">
        {/* Roles */}
        <Col md={6}>
          <Label className="text-uppercase small text-muted">Roles</Label>
          <Row>
            {opts.roles.map(r => (
              <Col xs={6} key={r.id} className="mb-2">
                <label className="d-flex align-items-center border rounded px-2 py-1 mb-0" style={{ cursor: "pointer" }}>
                  <Input type="checkbox" className="me-2 mt-0" checked={form.roles.includes(r.name)} onChange={() => toggle("roles", r.name)} />
                  {r.name}
                </label>
              </Col>
            ))}
            {!opts.roles.length && <Col><div className="text-muted small">No roles available.</div></Col>}
          </Row>
        </Col>

        {/* Access */}
        <Col md={6}>
          <Label className="text-uppercase small text-muted">Group access <span className="text-muted" style={{ textTransform: "none", fontSize: 10 }}>(whole group — incl. companies added later)</span></Label>
          <div className="mb-3">
            {opts.groups.map(g => (
              <label key={g.id} className="d-flex align-items-center border rounded px-2 py-2 mb-2" style={{ cursor: "pointer", background: form.group_ids.includes(g.id) ? "#f5f8ff" : "" }}>
                <Input type="checkbox" className="me-2 mt-0" checked={form.group_ids.includes(g.id)} onChange={() => toggle("group_ids", g.id)} />
                {cube(g.code)} {g.name}
              </label>
            ))}
            {!opts.groups.length && <div className="text-muted small">No groups.</div>}
          </div>
          <Label className="text-uppercase small text-muted">Company access <span className="text-muted" style={{ textTransform: "none", fontSize: 10 }}>(individual; greyed = already via group)</span></Label>
          <Row>
            {opts.companies.map(c => {
              const g = viaGroup(c)
              return (
                <Col xs={6} key={c.id} className="mb-2">
                  <label className="d-flex align-items-center border rounded px-2 py-1 mb-0" style={{ cursor: g ? "not-allowed" : "pointer", opacity: g ? 0.5 : 1 }}>
                    <Input type="checkbox" className="me-2 mt-0" disabled={g} checked={form.company_ids.includes(c.id) || g} onChange={() => toggle("company_ids", c.id)} />
                    {cube(c.code)} <span className="text-truncate">{c.company_name}</span>
                  </label>
                </Col>
              )
            })}
          </Row>
        </Col>
      </Row>

      <Row className="mt-3 align-items-end">
        <Col md={4}>
          <Label className="text-uppercase small text-muted">Home company</Label>
          <Input type="select" value={form.default_company_id || ""} onChange={e => set("default_company_id", e.target.value ? Number(e.target.value) : "")}>
            <option value="">No home company</option>
            {accessibleCompanies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </Input>
        </Col>
        <Col md={4}>
          <Label className="text-uppercase small text-muted">Status</Label>
          <Input type="select" value={form.status} onChange={e => set("status", Number(e.target.value))}>
            <option value={1}>Active</option>
            <option value={0}>Disabled</option>
          </Input>
        </Col>
        <Col md={4} className="text-end">
          <Button color="light" className="me-2" onClick={() => onDone?.(false)}>Cancel</Button>
          <Button color="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : isEdit ? "Save user" : "Add user"}</Button>
        </Col>
      </Row>
    </CardBody></Card>
  )
}

export default UserForm
